/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { deepCopy } from '@veeam-vspc/shared/core';
import {
    IdentityProviderAttributeMappingAttribute,
    IdentityProviderRoleMappingRuleRole,
} from '@veeam-vspc/models/rest';
import { useGlobalLang, useGlobalServices } from '@veeam-vspc/shared/components';

import type { IdentityProviderRoleMappingRule } from '@veeam-vspc/models/rest';
import type { WizardStep } from '@veeam-vspc/shared/components';
import type { VspcLang } from 'configs/languages';

import { PageWizard } from 'components/wizards/PageWizard';
import { getRuleInfoStep } from '../Base/components/RuleInfo';
import { getIdentityProviderStep } from '../Base/components/IdentityProvider';
import { getConditionsStep } from '../Base/components/Conditions';
import { ManagedCompaniesMode } from '../AdminUser/enums';
import { saveRule } from '../Base/helpers/saveRule';
import { getRoleStep } from './components/Role';
import { getCompaniesStep } from '../Base/components/Companies';
import { getSummaryStep } from './components/Summary';
import { getInitialProviderName } from '../Base/helpers/getInitialProviderName';

import type { SSORuleModel } from '../Base/interfaces/sso-rule-model';
import type { SSORuleProps } from '../Base/interfaces/sso-rule-props';

export const SPSSORule = (props: SSORuleProps) => {
    const lang = useGlobalLang<VspcLang>();
    const { transportService } = useGlobalServices();
    const defaultModel: IdentityProviderRoleMappingRule = {
        name: '',
        role: IdentityProviderRoleMappingRuleRole.ResellerAdministrator,
        additionalMappings: [],
        attributeMappings: [
            {
                claimType: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/firstName',
                allowAliases: true,
                attribute: IdentityProviderAttributeMappingAttribute.FirstName,
            },
            {
                claimType: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/lastName',
                allowAliases: true,
                attribute: IdentityProviderAttributeMappingAttribute.LastName,
            },
            {
                claimType: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
                allowAliases: true,
                attribute: IdentityProviderAttributeMappingAttribute.Name,
            },
            {
                claimType: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/address',
                allowAliases: true,
                attribute: IdentityProviderAttributeMappingAttribute.Address,
            },
            {
                claimType: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/phone',
                allowAliases: true,
                attribute: IdentityProviderAttributeMappingAttribute.Phone,
            },
        ],
        managedCompaniesUids: [],
        organizationMappingSourceClaimType: '',
    };

    const title = props.isEdit ? lang.EDIT_AUTHORIZATION_RULE : lang.NEW_AUTHORIZATION_RULE;
    const rolesEnum = IdentityProviderRoleMappingRuleRole;

    return (
        <PageWizard
            title={title}
            deactivate={() => {}}
            isEdit={props.isEdit}
            data={{
                model: props.isEdit ? deepCopy(props.model) : defaultModel,
                providers: props.providers,
                rules: props.rules,
                identityProviderName: getInitialProviderName(props.providers),
                selectAllCompanies: props.selectAllCompanies ? ManagedCompaniesMode.AllCompanies : ManagedCompaniesMode.CustomSelection,
                wizard: props.wizard,
            } as SSORuleModel}
            steps={[
                getRuleInfoStep(lang, lang.RULE_INFO),
                getIdentityProviderStep(lang.IDENTITY_PROVIDER),
                getRoleStep(lang.ROLE),
                getCompaniesStep(lang.COMPANIES),
                getConditionsStep(lang, lang.CONDITIONS),
                getSummaryStep(lang.SUMMARY),
            ] as WizardStep<SSORuleModel>[]}
            onFinish={async({ data }) => {
                const isRoleWithManagedCompanies = [rolesEnum.ResellerOperator, rolesEnum.ResellerUser].includes(data.model.role);
                const shouldSendCompaniesUids = isRoleWithManagedCompanies && data.selectAllCompanies === ManagedCompaniesMode.CustomSelection;

                const model: IdentityProviderRoleMappingRule = {
                    ...data.model,
                    managedCompaniesUids: shouldSendCompaniesUids ? data.model.managedCompaniesUids : undefined,
                    manageAllCompanies: isRoleWithManagedCompanies
                        ? data.selectAllCompanies === ManagedCompaniesMode.AllCompanies
                        : true,
                    organizationMappingSourceClaimType: data.model.organizationMappingSourceClaimType.trim(),
                    additionalMappings: data.model.additionalMappings.map(mapping => ({
                        ...mapping,
                        value: mapping.value.trim(),
                        claimType: mapping.claimType.trim(),
                    })),
                };

                return saveRule(transportService, { title, data, props, model });
            }}
        />
    );
};
